import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const fetchSkipAnalysis = createAsyncThunk(
  "filingRules/fetchSkipAnalysis",
  async (
    { category, removeNotSetup, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const { data } = await axios.post(
        `${baseURL}/emis_pathology/api/skip_analysis`,
        {
          category,
          remove_notsetup: removeNotSetup,
          license_key,
        },
      );
      return data;
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchLatest100DaySummaries = createAsyncThunk(
  "filingRules/fetchLatest100DaySummaries",
  async (license_key, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${baseURL}/emis_pathology/api/filing_summary_last_100_days`,
        { license_key },
      );
      return data;
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchFilingRules = createAsyncThunk(
  "filingRules/fetchFilingRules",
  async (license_key, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${baseURL}/emis_pathology/api/filing_rules`,
        { license_key },
      );
      return data;
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }

      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const updateFilingRuleStatus = createAsyncThunk(
  "filingRules/updateFilingRuleStatus",
  async (
    { categories, status, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      await axios.post(`${baseURL}/emis_pathology/api/update_status`, {
        categories,
        status,
        license_key,
      });
      // Reload the rules after successful update
      await dispatch(fetchFilingRules(license_key));
      return { success: true };
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const deleteFilingRuleStatus = createAsyncThunk(
  "filingRules/deleteFilingRuleStatus",
  async (
    { categories, rows_data, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      await axios.post(`${baseURL}/emis_pathology/api/rule/delete/`, {
        categories,
        rows_data,
        license_key,
      });
      await dispatch(fetchFilingRules(license_key));
      return { success: true };
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const exportFilingRulesToExcel = createAsyncThunk(
  "filingRules/exportToExcel",
  async (license_key, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${baseURL}/emis_pathology/api/filing_rules/export`,
        { license_key },
        {
          responseType: "blob", // Important for file download
        },
      );

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers["content-disposition"];
      let filename = "filing_rules_export.xlsx";

      if (contentDisposition) {
        // RFC 5987 (filename*=)
        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i,
        );
        if (utf8Match && utf8Match[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          // Normal filename=
          const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
          if (asciiMatch && asciiMatch[1]) {
            filename = asciiMatch[1];
          }
        }
      }
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      if (err.response && err.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const filingRulesSlice = createSlice({
  name: "filingRules",
  initialState: {
    rules: [],
    summaries: {},
    loading: false,
    error: null,
    selected: [],
    updating: false,
    exporting: false,
    skipAnalysis: [],
    analysisLoading: false,
  },
  reducers: {
    setSelected: (state, action) => {
      state.selected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSkipAnalysis.pending, (state) => {
        state.analysisLoading = true;
      })
      .addCase(fetchSkipAnalysis.fulfilled, (state, action) => {
        state.analysisLoading = false;
        state.skipAnalysis = action.payload;
      })
      .addCase(fetchSkipAnalysis.rejected, (state) => {
        state.analysisLoading = false;
      })
      .addCase(fetchLatest100DaySummaries.fulfilled, (state, action) => {
        state.summaries = action.payload;
      })
      .addCase(fetchFilingRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFilingRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload;
      })
      .addCase(fetchFilingRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateFilingRuleStatus.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateFilingRuleStatus.fulfilled, (state) => {
        state.updating = false;
        state.selected = [];
      })
      .addCase(updateFilingRuleStatus.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      })
      .addCase(deleteFilingRuleStatus.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(deleteFilingRuleStatus.fulfilled, (state) => {
        state.updating = false;
        state.selected = [];
      })
      .addCase(deleteFilingRuleStatus.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      })
      .addCase(exportFilingRulesToExcel.pending, (state) => {
        state.exporting = true;
        state.error = null;
      })
      .addCase(exportFilingRulesToExcel.fulfilled, (state) => {
        state.exporting = false;
      })
      .addCase(exportFilingRulesToExcel.rejected, (state, action) => {
        state.exporting = false;
        state.error = action.payload;
      });
  },
});

export const { setSelected } = filingRulesSlice.actions;
export default filingRulesSlice.reducer;
